import { createRouter, createWebHistory } from "vue-router";
import { trackPageView } from "@/analytics/app-analytics";
import { SaveState, discardUnsavedChanges } from "@/stores/SaveDrawingState";

// LAYOUTS
import AuthLayout from "./views/layouts/AuthLayout.vue";
import WelcomeLayout from "./views/layouts/WelcomeLayout.vue";
import CanvaLayout from "./views/layouts/CanvaLayout.vue";

import SettingsLayout from "./views/layouts/SettingsLayout.vue";

// PAGES
import HomepageView from "./views/HomepageView.vue";
import DrawingCanvasView from "./views/DrawingCanvasView.vue";
import Login from "./components/welcome/Login.vue";
import ArchiveListView from "@/views/ArchiveListView.vue";
import SyncListView from "@/views/SyncListView.vue";
import Forgot from "@/components/welcome/Forgot.vue";
import Reset from "@/components/welcome/Reset.vue";
import UserListView from "./components/admin/UserListView.vue";
import UserRegistration from "./components/admin/UserRegistration.vue";
import LegendsListView from "./views/LegendsListView.vue";
import LegendUpdateView from "./views/LegendUpdateView.vue";
import LegendCreateView from "./views/LegendCreateView.vue";

import ProjectsListView from "./views/ProjectsListView.vue";
import ProjectView from "./views/ProjectView.vue";

import TemplatesListView from "./views/TemplatesListView.vue";
import ReportLayout from "./views/layouts/ReportLayout.vue";

import FieldObservationsLayout from "./views/layouts/FieldObservationsLayout.vue";
import DiagnosticView from "./views/DiagnosticView.vue";
import FieldPartTemplatesView from "./views/FieldPartTemplatesView.vue";

enum Access {
  AUTH = "Authenticated",
  NO_AUTH = "NoAuth",
}

export const noAuthRoutePrefix = "/bienvenue";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: noAuthRoutePrefix,
      redirect: `${noAuthRoutePrefix}/se-connecter`,
      name: "welcome",
      component: WelcomeLayout,

      meta: {
        access: Access.NO_AUTH,
      },
      children: [
        {
          path: "se-connecter",
          name: "login",
          component: Login,
        },
        {
          path: "recuperation-de-mot-de-passe",
          name: "forgot",
          alias: "forgot",
          component: Forgot,
        },
        {
          path: "nouveau-mot-de-passe",
          name: "reset",
          component: Reset,
          props: (route) => ({ token: route.query.token }),
        },
      ],
    },
    // redirect. `/reset` will be in email templates. Therefore, the human-readable path in French can be updated without breaking emails
    {
      path: "/",
      component: AuthLayout,
      redirect: { name: "home" },
      meta: {
        access: Access.AUTH,
      },
      children: [
        { path: "/reset", redirect: { name: "reset" } },
        {
          path: "/home",
          name: "home",
          component: HomepageView,

          meta: {
            title: "dashboard:navigation.home",
          },
        },
        {
          path: "/legends",
          redirect: { name: "legend-create" },
          children: [
            {
              path: ":id",
              name: "legend-update",
              component: LegendUpdateView,
              meta: {
                title: "dashboard:navigation.edit-legend",
              },
            },
            {
              path: "create",
              name: "legend-create",
              component: LegendCreateView,
              meta: {
                title: "dashboard:navigation.new-legend",
              },
            },
          ],
        },

        {
          path: "/settings",
          name: "settings",
          component: SettingsLayout,
          redirect: { name: "settings-legends" },
          meta: {
            title: "dashboard:navigation.organization-page",
          },
          children: [
            {
              path: "members",
              name: "settings-members",
              component: UserListView,
            },
            {
              path: "new-member",
              name: "settings-new-member",
              component: UserRegistration,
            },
            {
              path: "legends",
              name: "settings-legends",
              component: LegendsListView,
            },
            {
              path: "templates",
              name: "settings-templates",
              component: TemplatesListView,
            },
            {
              path: "part-templates",
              name: "settings-part-templates",
              component: FieldPartTemplatesView,
            },
          ],
        },
        // Legacy routes - redirect to new settings routes
        {
          path: "/my-organization",
          redirect: { name: "settings-members" },
        },
        {
          path: "/synchronization-issues",
          name: "synchronization-issues",
          component: SyncListView,
          meta: {
            title: "dashboard:navigation.synchronization-issues",
          },
        },
        {
          path: "/archives",
          name: "archives",
          component: ArchiveListView,

          meta: {
            title: "dashboard:navigation.archives",
          },
        },
        {
          path: "/projects",
          name: "projects",
          component: ProjectsListView,

          meta: {
            title: "dashboard:navigation.projects",
          },
        },
        {
          path: "/projects/:id",
          name: "project",
          component: ProjectView,

          meta: {
            title: "dashboard:navigation.project",
          },
        },
        {
          path: "/templates",
          name: "templates",
          redirect: { name: "settings-templates" },
        },
        {
          path: "/diagnostic/:id",
          name: "diagnostic",
          component: DiagnosticView,

          meta: {
            title: "dashboard:navigation.diagnostic",
          },
        },
      ],
    },
    {
      path: "/dessin",
      component: CanvaLayout,
      meta: {
        access: Access.AUTH,
      },
      children: [
        {
          path: "/dessin/:drawingId?",
          name: "canvas",
          component: DrawingCanvasView,

          props: (route) => {
            /**
             * This would preserve the other route.params object properties overriding only
             * `userId` in case it exists with its integer equivalent, or otherwise with
             * undefined.
             */
            return {
              ...route.params,
              // drawingId: route.params.drawingId,
              drawingId:
                Number.parseInt(route.params.drawingId as string, 10) ||
                undefined,
            };
          },
        },
      ],
    },
    {
      path: "/report/:reportId",
      name: "report-tool",
      component: ReportLayout,
      meta: {
        access: Access.AUTH,
      },
    },
    {
      path: "/field-observations/:diagnosticId",
      name: "field-observations",
      component: FieldObservationsLayout,
      meta: {
        access: Access.AUTH,
      },
    },
  ],
  scrollBehavior(/*to, from, savedPosition*/) {
    return { top: 0 }; // To get on top of the page especially for canvas
  },
});

// Navigation guard for unsaved changes in canvas
router.beforeEach((to, from) => {
  // Check if navigating away from canvas with unsaved changes
  const isLeavingCanvas = from.name === "canvas" && to.name !== "canvas";

  if (isLeavingCanvas && SaveState.value === "changed") {
    const confirmLeave = window.confirm(
      "Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter ?",
    );

    if (!confirmLeave) {
      return false; // Cancel navigation
    }

    // User confirmed : mark as saved (skip double save) + purge l'oplog pending.
    discardUnsavedChanges();
  }

  return true;
});

// One event per page displayed. Guarded on the route NAME changing:
// afterEach also fires for query-only updates (tab sync, filters in the URL),
// which are not new page views. Ids stay out — the name is the page.
router.afterEach((to, from) => {
  if (typeof to.name === "string" && to.name !== from.name) {
    trackPageView(to.name);
  }
});

export default router;
